import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { categories, tasksVersion, navigate } from '../store.js';
import { __ } from '../i18n.js';
import Icon from './Icon.jsx';
import { formatMinutes, deadlineChipLabel } from '../lib/format.js';
import { categoryIconName, PRIORITY_LEVELS } from '../lib/labels.js';
import { sortUpcomingTasks, isDeadlineAtRisk } from '../lib/upcoming.js';

// Must match theme.css's `@media (min-width: 1024px)` for .etm-shell —
// kept as a documented duplicate of one magic number rather than a shared
// build-time constant between a CSS media query and a JS matchMedia string.
const DESKTOP_QUERY = '(min-width: 1024px)';
const RETRY_DELAY_MS = 5000;

function resolveCategory(categoryId) {
  return categories.value.find((c) => c.id === categoryId) || null;
}

/**
 * WP-2: the desktop-only "Aankomend" sidebar (see theme.css .etm-shell /
 * .etm-upcoming-panel — display:none below the 1024px breakpoint, so this
 * component's own root contributes zero box on phones/tablets). Reuses
 * GET /tasks?status=active (no new endpoint) and sorts/filters entirely
 * client-side per the brief.
 *
 * Always rendered in the DOM regardless of viewport — only the *fetch* is
 * gated behind matchMedia, so phones never pay for a request whose result
 * they'd never show (the brief's own instruction).
 */
export default function UpcomingPanel() {
  const [tasks, setTasks] = useState(null); // null = never loaded yet
  const [loadError, setLoadError] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const version = tasksVersion.value; // reading .value here subscribes this component to bumps

  useEffect(() => {
    let cancelled = false;
    let retried = false;
    let retryTimer = null;
    // WP-2 review finding: onFocus/onMediaChange can fire load() again
    // while an earlier call is still in flight (tab refocus during a slow
    // request, a breakpoint crossing right after a retry). Without a
    // sequence token, a slow earlier response resolving *after* a faster
    // later one would silently overwrite fresher data with stale data —
    // this counter makes only the most-recently-issued call's response
    // ever apply.
    let latestRequestId = 0;
    const mql = window.matchMedia(DESKTOP_QUERY);

    function load() {
      if (!mql.matches) return;
      const requestId = ++latestRequestId;
      api.tasks
        .list({ status: 'active' })
        .then((res) => {
          if (cancelled || requestId !== latestRequestId) return;
          setTasks(res.tasks || []);
          setLoadError(false);
        })
        .catch(() => {
          if (cancelled || requestId !== latestRequestId) return;
          // Subtle inline error only — never a toast, this panel must
          // never interrupt the main flow. One bounded silent retry;
          // a real outage shouldn't turn into an infinite poll loop.
          setLoadError(true);
          if (!retried) {
            retried = true;
            retryTimer = setTimeout(load, RETRY_DELAY_MS);
          }
        });
    }

    load();

    // A fresh user-driven trigger (tab refocus, resizing across the
    // breakpoint) deserves its own retry budget, not one silently
    // suppressed by an earlier unrelated failure — and must cancel any
    // retry that earlier failure already scheduled (WP-2 review finding:
    // an uncleared timer could otherwise fire a redundant load() and
    // resurrect the error banner well after the user's own refresh
    // already succeeded).
    function onFocus() {
      retried = false;
      clearTimeout(retryTimer);
      load();
    }
    function onMediaChange(e) {
      if (e.matches) {
        retried = false;
        clearTimeout(retryTimer);
        load();
      }
    }

    window.addEventListener('focus', onFocus);
    mql.addEventListener('change', onMediaChange);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      window.removeEventListener('focus', onFocus);
      mql.removeEventListener('change', onMediaChange);
    };
  }, [version]);

  const filtered = filterCategoryId ? (tasks || []).filter((t) => t.category_id === filterCategoryId) : tasks || [];
  const sorted = sortUpcomingTasks(filtered);

  return (
    <aside class="etm-upcoming-panel" aria-label={__('Aankomend', 'etm')}>
      <h2 class="etm-upcoming-panel__title">{__('Aankomend', 'etm')}</h2>

      <div class="etm-filters">
        <button
          type="button"
          class={`etm-filter-btn${filterCategoryId === null ? ' active' : ''}`}
          onClick={() => setFilterCategoryId(null)}
        >
          {__('Alle', 'etm')}
        </button>
        {categories.value.map((cat) => (
          <button
            type="button"
            key={cat.id}
            class={`etm-filter-btn${filterCategoryId === cat.id ? ' active' : ''}`}
            onClick={() => setFilterCategoryId(cat.id)}
          >
            <Icon name={categoryIconName(cat.icon)} size={16} /> {cat.name}
          </button>
        ))}
      </div>

      {tasks === null ? (
        <p class="etm-field-hint">
          {loadError ? __('Kon taken niet laden.', 'etm') : __('Bezig met laden…', 'etm')}
        </p>
      ) : (
        <>
          {loadError ? <p class="etm-field-hint">{__('Vernieuwen is niet gelukt.', 'etm')}</p> : null}
          {sorted.length === 0 ? (
            <p class="etm-field-hint">{__('Geen openstaande taken.', 'etm')}</p>
          ) : (
            <div class="etm-task-list">
              {sorted.map((task) => {
                const cat = resolveCategory(task.category_id);
                const deadlineLabel = task.deadline ? deadlineChipLabel(task.deadline) : null;
                const atRisk = isDeadlineAtRisk(task);
                const priorityLevel = PRIORITY_LEVELS.find((p) => p.value === Number(task.priority));
                const priorityValue = Number(task.priority) || 1;

                return (
                  <div
                    key={task.id}
                    class={`etm-task-list-item etm-upcoming-row etm-upcoming-row--p${priorityValue}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`${task.title}${priorityLevel ? `, ${priorityLevel.label()}` : ''}`}
                    onClick={() => navigate('taskForm', { task })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('taskForm', { task });
                      }
                    }}
                  >
                    <span class="etm-category-icon etm-category-icon--inline">
                      <Icon name={categoryIconName(cat?.icon)} size={18} />
                    </span>
                    <div class="etm-task-list-item__body">
                      <div class="etm-task-list-item__title">{task.title}</div>
                      <div class="etm-task-list-item__meta">
                        <span class="etm-chip">{formatMinutes(task.effort_minutes)}</span>
                        {deadlineLabel ? (
                          <span class={`etm-chip${atRisk ? ' etm-chip--danger' : ''}`}>
                            <Icon name="calendar_month" size={14} /> {deadlineLabel}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </aside>
  );
}
